import React, { useCallback } from 'react';
import { Editor } from '@tiptap/react';
import {
  Bold, Italic, Underline, Heading1, Heading2, Heading3,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Link, Unlink, Undo2, Redo2,
} from 'lucide-react';

interface EmailEditorToolbarProps {
  editor: Editor;
}

const EmailEditorToolbar: React.FC<EmailEditorToolbarProps> = ({ editor }) => {
  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const btn = (
    active: boolean,
    onClick: () => void,
    icon: React.ReactNode,
    title: string
  ) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded transition-colors ${
        active
          ? 'bg-indigo-100 text-indigo-700'
          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
      }`}
    >
      {icon}
    </button>
  );

  const iconSize = 16;
  const sep = <div className="w-px h-5 bg-gray-200 mx-1" />;

  return (
    <div className="flex items-center gap-0.5 flex-wrap px-3 py-2 border-b border-gray-200 bg-gray-50">
      {btn(false, () => editor.chain().focus().undo().run(), <Undo2 size={iconSize} />, 'Undo')}
      {btn(false, () => editor.chain().focus().redo().run(), <Redo2 size={iconSize} />, 'Redo')}

      {sep}

      {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), <Bold size={iconSize} />, 'Bold')}
      {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), <Italic size={iconSize} />, 'Italic')}
      {btn(editor.isActive('underline'), () => editor.chain().focus().toggleUnderline().run(), <Underline size={iconSize} />, 'Underline')}

      {sep}

      {btn(editor.isActive('heading', { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), <Heading1 size={iconSize} />, 'Heading 1')}
      {btn(editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), <Heading2 size={iconSize} />, 'Heading 2')}
      {btn(editor.isActive('heading', { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), <Heading3 size={iconSize} />, 'Heading 3')}

      {sep}

      {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), <List size={iconSize} />, 'Bullet List')}
      {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), <ListOrdered size={iconSize} />, 'Ordered List')}

      {sep}

      {btn(editor.isActive({ textAlign: 'left' }), () => editor.chain().focus().setTextAlign('left').run(), <AlignLeft size={iconSize} />, 'Align Left')}
      {btn(editor.isActive({ textAlign: 'center' }), () => editor.chain().focus().setTextAlign('center').run(), <AlignCenter size={iconSize} />, 'Align Center')}
      {btn(editor.isActive({ textAlign: 'right' }), () => editor.chain().focus().setTextAlign('right').run(), <AlignRight size={iconSize} />, 'Align Right')}

      {sep}

      {btn(editor.isActive('link'), setLink, <Link size={iconSize} />, 'Add Link')}
      {btn(false, () => editor.chain().focus().unsetLink().run(), <Unlink size={iconSize} />, 'Remove Link')}
    </div>
  );
};

export default EmailEditorToolbar;
